<template>
  <div class="borrow">
    <div class="box box-primary">
      <div class="box-header with-border">
        <h3 class="box-title">
          {{ $t('menu.trash') }}
        </h3>
        <div class="pull-right box-tools d-sm-none d-md-block">
          <template v-if="can([Permissions.backend.trashed.export])">
            <a
              :href="`${baseUrl}/export/trash`"
              class="btn btn-info btn-sm"
            >
              <i class="fa fa-file-excel-o" /> {{ $t('label.exportAsExcel') }}
            </a>
          </template>
        </div>
      </div>
      <div class="box-body">
        <div class="row">
          <div class="dataTables_row_up">
            <div class="col-xs-8 col-sm-6 margin-bottom">
              <div class="btn-group">
                <button
                  class="btn btn-default"
                  type="button"
                >
                  {{ $t('label.showOrHideColumns') }}
                </button>
                <button
                  aria-expanded="false"
                  aria-haspopup="true"
                  class="btn btn-default dropdown-toggle"
                  data-toggle="dropdown"
                  type="button"
                >
                  <span class="caret" />
                  <span class="sr-only">Toggle Dropdown</span>
                </button>
                <ul
                  aria-labelledby="showColumnRow"
                  class="dropdown-menu"
                >
                  <template v-for="(column, columnIndex) in columns">
                    <li
                      :key="columnIndex"
                      @click="toggleColumn(columnIndex)"
                    >
                      <a
                        :class="`${(!oTable || oTable.columns().visible()[columnIndex]) ? 'text-blue' : 'text-red'}`"
                        href="javascript:void(0)"
                      >
                        <i
                          :class="`fa fa-${(!oTable || oTable.columns().visible()[columnIndex]) ? 'eye' : 'eye-slash'}`"
                        />
                        {{ $t(column.titleKey) }}
                      </a>
                    </li>
                  </template>
                </ul>
              </div>
            </div>
            <div class="col-xs-4 col-sm-6 margin-bottom d-md-none d-sm-block">
              <div class="pull-right box-tools">
                <a
                  :href="`${baseUrl}/export/pending`"
                  class="btn btn-info btn-sm"
                >
                  <i class="fa fa-file-excel-o" /> {{ $t('label.exportAsExcel') }}
                </a>
              </div>
            </div>
          </div>
          <div class="col-md-12">
            <div>
              <table
                id="borrow-table"
                class="table table-hover table-striped table-bordered"
              >
                <tbody />
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <comment-modal
      :route-view-all-comments="{
        name: 'show-trashed',
        params: {
          borrow_id
        }
      }"
    />
    <assign-loan-officer-modal @refreshList="oTable.draw()" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import CommentModal from '@/views/comments/components/CommentModal'
import AssignLoanOfficerModal from '@/views/borrows/_components/AssignLoanOfficerModal'
import Permissions from '@/permissions'

export default {
  name: 'List',
  components: { AssignLoanOfficerModal, CommentModal },
  metaInfo () {
    return {
      title: this.$t('menu.trash'),
      titleTemplate: '%s | ' + process.env.VUE_APP_NAME
    }
  },
  data () {
    return {
      baseUrl: process.env.VUE_APP_API,
      oTable: null,
      Permissions
    }
  },
  computed: {
    ...mapState({
      borrow_id: state => state.comment.borrow_id
    }),
    columns () {
      return [
        {
          data: 'action',
          name: 'action',
          searchable: false,
          orderable: false,
          titleKey: 'table.action',
          title: this.$t('table.actions')
        },
        {
          data: 'deleted_at',
          name: 'deleted_at',
          searchable: false,
          titleKey: 'table.deletedAt',
          title: this.$t('table.deletedAt'),
          width: '8%'
        },
        {
          data: 'loan_officers',
          name: 'loan_officers',
          title: this.$t('table.loan_officer'),
          titleKey: 'table.loan_officer',
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        { data: 'name', name: 'name', titleKey: 'table.name', title: this.$t('table.name'), width: '8%' },
        { data: 'phone', name: 'phone', titleKey: 'table.phone', title: this.$t('table.phone'), width: '8%' },
        { data: 'amount_applies', name: 'amount_applies', titleKey: 'table.amount_applies', title: this.$t('table.amount_applies'), width: '8%' },
        { data: 'type', name: 'type', titleKey: 'table.type', title: this.$t('table.type'), width: '8%' },
        {
          data: 'amount',
          name: 'amount',
          titleKey: 'table.amount',
          title: this.$t('table.amount'),
          width: '8%',
          render: (data) => `${this.currentFormat(data)}`
        },
        {
          data: 'duration',
          name: 'duration',
          titleKey: 'table.duration',
          title: this.$t('table.duration'),
          width: '8%',
          render: (data, type, row) => row.loan_type === 'Payday' ? `${data} Day(s)` : `${data} Month(s)`
        },
        {
          data: 'gross_monthly_income',
          name: 'gross_monthly_income',
          titleKey: 'table.grossMonthlyIncome',
          title: this.$t('table.grossMonthlyIncome'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'gender',
          name: 'gender',
          titleKey: 'table.gender',
          title: this.$t('table.gender'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'date_of_birth',
          name: 'date_of_birth',
          titleKey: 'table.dateOfBirth',
          title: this.$t('table.dateOfBirth'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        { data: 'age', name: 'age', titleKey: 'table.age', title: this.$t('table.age'), width: '8%' },
        {
          data: 'marital_status',
          name: 'marital_status',
          titleKey: 'table.maritalStatus',
          title: this.$t('table.maritalStatus'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'loanTypeName',
          name: 'loanTypeName',
          titleKey: 'table.purposeLoan',
          title: this.$t('table.purposeLoan'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'occupation',
          name: 'occupation',
          titleKey: 'table.occupation',
          title: this.$t('table.occupation'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'company_type',
          name: 'company_type',
          titleKey: 'table.companyType',
          title: this.$t('table.companyType'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'company_name',
          name: 'company_name',
          titleKey: 'table.companyName',
          title: this.$t('table.companyName'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'company_contact_name',
          name: 'company_contact_name',
          titleKey: 'table.companyContactName',
          title: this.$t('table.companyContactName'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'company_contact_phone',
          name: 'company_contact_phone',
          titleKey: 'table.companyContactPhone',
          title: this.$t('table.companyContactPhone'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'relative_type',
          name: 'relative_type',
          titleKey: 'table.relativeType',
          title: this.$t('table.relativeType'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'relative_name',
          name: 'relative_name',
          titleKey: 'table.relativeName',
          title: this.$t('table.relativeName'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'relative_contact_phone',
          name: 'relative_contact_phone',
          titleKey: 'table.relativeContactPhone',
          title: this.$t('table.relativeContactPhone'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'province',
          name: 'province',
          titleKey: 'table.province',
          title: this.$t('table.province'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'district',
          name: 'district',
          titleKey: 'table.district',
          title: this.$t('table.district'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'commune',
          name: 'commune',
          titleKey: 'table.commune',
          title: this.$t('table.commune'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'borrowEducationName',
          name: 'borrowEducationName',
          titleKey: 'table.borrowEducationId',
          title: this.$t('table.borrowEducationId'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'school_name',
          name: 'school_name',
          titleKey: 'table.schoolName',
          title: this.$t('table.schoolName'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'study_major',
          name: 'study_major',
          titleKey: 'table.studyMajor',
          title: this.$t('table.studyMajor'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'graduate_year',
          name: 'graduate_year',
          titleKey: 'table.graduateYear',
          title: this.$t('table.graduateYear'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'email',
          name: 'email',
          titleKey: 'table.email',
          title: this.$t('table.email'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'address',
          name: 'address',
          titleKey: 'table.address',
          title: this.$t('table.address'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'national_id_number',
          name: 'national_id_number',
          titleKey: 'table.nationalIdNumber',
          title: this.$t('table.nationalIdNumber'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'how_long_have_been_living_there',
          name: 'how_long_have_been_living_there',
          titleKey: 'table.howLongHaveBeenLivingThere',
          title: this.$t('table.howLongHaveBeenLivingThere'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'bank_name',
          name: 'bank_name',
          titleKey: 'table.bankName',
          title: this.$t('table.bankName'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'bank_account_number',
          name: 'bank_account_number',
          titleKey: 'table.bankAccountNumber',
          title: this.$t('table.bankAccountNumber'),
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'facebook_name',
          name: 'facebook_name',
          titleKey: 'table.facebookName',
          title: this.$t('table.facebookName'),
          visible: false,
          width: '8%',
          render: (data) => data || this.$t('string.na')
        }
      ]
    }
  },
  watch: {
    '$i18n.locale' () {
      this.oTable.destroy()
      this.oTable = this.dataTable()
      this.oTable.draw(true)
    },
    oTable () {
      this.enabledAction()
    }
  },
  methods: {
    toggleColumn (column) {
      column = this.oTable.column(column)
      column.visible(!column.visible())
    },
    dataTable () {
      const self = this
      return $('#borrow-table').DataTable({
        stateSave: true,
        processing: true,
        serverSide: true,
        scrollX: true,
        scrollCollapse: true,
        pageLength: process.env.VUE_APP_PAGE_LENGTH,
        lengthMenu: this.$lengthMenu,
        ajax: {
          method: 'POST',
          url: process.env.VUE_APP_API + '/api/v4/backend/trashed/datatable',
          data: (d) => {
            d.status = 'Trash'
            d.labelShow = self.$t('label.show')
            d.labelApproved = self.$t('label.approved')
            d.labelRejected = self.$t('label.rejected')
            d.labelDelete = self.$t('label.delete')
            d.noPermission = self.$t('string.noPermission')
            d.assignLoanOfficerLabel = self.$t('string.assignLoanOfficer')
            d.commentLabel = self.$t('string.comment')
            d.na = self.$t('string.na')
          },
          error: (xhr) => {
            this.onAccessDenied(xhr)
          }
        },
        columns: self.columns,
        order: [
          [1, 'desc']
        ],
        language: {
          url: `/locale/${this.$i18n.locale}.json`
        }
      })
    },
    enabledAction () {
      this.clearEventHandlers([
        '.btn-comment',
        '.btn-assign-loan-officer'
      ])
      const self = this
      // comment
      $(document).on('click', '.btn-comment', function (e) {
        e.preventDefault()
        self.$store.commit('comment/openCommentModal')
        self.$store.commit('comment/setBorrowId', $(this).attr('data-borrow'))
      })
      // assign loan officer
      $(document).on('click', '.btn-assign-loan-officer', function (e) {
        e.preventDefault()
        self.$store.commit('loanOfficerAssignment/toggleAssignLoanOfficerModal')
        self.$store.commit('loanOfficerAssignment/setBorrowId', $(this).attr('data-borrow'))
      })
    }
  },
  mounted () {
    this.oTable = this.dataTable()
    this.enabledAction()
  },
  beforeDestroy () {
    this.oTable.clear()
    this.oTable.destroy()
  }
}
</script>

<style scoped>

</style>
